// Simulación de base de datos en localStorage para desarrollo
// En producción esto se conectaría a MongoDB real

// Correo del administrador del sistema (debe coincidir con SYSTEM_ADMIN_EMAIL del backend). Sin definir, no hay admin de sistema.
const SYSTEM_ADMIN_EMAIL = process.env.REACT_APP_SYSTEM_ADMIN_EMAIL || 'sin-administrador-de-sistema';

export interface Student {
  _id: string;
  id: string; // RUT o ID único
  name: string;
  firstName?: string;
  lastName?: string;
  course: string;
  email?: string;
  birthDate?: string;
  gender?: 'M' | 'F' | 'Otro';
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  // Datos de evaluación
  dimensions?: {
    [dimensionId: string]: {
      skills: {
        [skillId: string]: {
          levelIndex: number;
          lastUpdated: string;
          updatedBy: string;
        };
      };
    };
  };
}

export interface Teacher {
  _id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'teacher' | 'viewer';
  organizationId: string;
  courses: string[];
  phone?: string;
  isActive: boolean;
  mustChangePassword: boolean;
  password: string; // En producción esto estaría hasheado
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Course {
  _id: string;
  name: string;
  level: string;
  organizationId: string;
  teacherIds: string[];
  studentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationData {
  _id: string;
  tenantKey: string;
  displayName: string;
  domains: string[];
  status: 'active' | 'suspended' | 'trial' | 'inactive';
  createdAt: string;
  updatedAt: string;
  branding: {
    logoUrl: string;
    logoBase64?: string; // Logo almacenado como base64 para exportación PDF
    faviconUrl: string;
    colors: {
      primary: string;
      secondary: string;
      accent?: string;
    };
    logoSize?: {
      coverWidth: number; // Ancho del logo en portada (mm)
      headerWidth: number; // Ancho del logo en header de páginas internas (mm)
    };
  };
  settings: {
    maxUsers: number;
    maxStudents: number;
    features: string[];
  };
  // Estadísticas calculadas
  userCount: number;
  studentCount: number;
  courseCount: number;
}

class DatabaseService {
  private getStorageKey(collection: string, organizationId?: string): string {
    if (organizationId) {
      return `csn_${organizationId}_${collection}`;
    }
    return `csn_system_${collection}`;
  }

  // Organizaciones
  async getOrganizations(): Promise<OrganizationData[]> {
    // Si es administrador del sistema, usar API real
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Verificar si es admin del sistema sin decodificar token (evitar error atob)
        const API_BASE_URL = process.env.REACT_APP_API_URL || 
          (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');
        const response = await fetch(`${API_BASE_URL}/api/organizations`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('✅ Organizaciones cargadas desde API:', data.organizations.length);
          return data.organizations || [];
        } else {
          console.warn('⚠️ Error cargando organizaciones desde API:', response.statusText);
        }
      } catch (error) {
        console.warn('⚠️ Error verificando API organizaciones:', error);
      }
    }
    
    // Fallback a localStorage
    const data = localStorage.getItem(this.getStorageKey('organizations'));
    const organizations = data ? JSON.parse(data) : [];
    
    // Calcular estadísticas para cada organización (solo si está usando localStorage)
    for (const org of organizations) {
      const teachers = await this.getTeachers(org._id);
      const students = await this.getStudents(org._id);
      const courses = await this.getCourses(org._id);
      
      org.userCount = teachers.length;
      org.studentCount = students.length;
      org.courseCount = courses.length;
    }
    
    return organizations;
  }

  async getOrganization(tenantKey: string): Promise<OrganizationData | null> {
    const organizations = await this.getOrganizations();
    return organizations.find(org => org.tenantKey === tenantKey) || null;
  }

  async createOrganization(orgData: Omit<OrganizationData, '_id' | 'userCount' | 'studentCount' | 'courseCount'>): Promise<OrganizationData> {
    const organizations = await this.getOrganizations();
    
    const newOrg: OrganizationData = {
      ...orgData,
      _id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
      userCount: 0,
      studentCount: 0,
      courseCount: 0
    };
    
    organizations.push(newOrg);
    localStorage.setItem(this.getStorageKey('organizations'), JSON.stringify(organizations));
    
    return newOrg;
  }

  async updateOrganization(tenantKey: string, updates: Partial<OrganizationData>): Promise<OrganizationData | null> {
    const organizations = await this.getOrganizations();
    const index = organizations.findIndex(org => org.tenantKey === tenantKey);
    
    if (index === -1) return null;
    
    organizations[index] = {
      ...organizations[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    localStorage.setItem(this.getStorageKey('organizations'), JSON.stringify(organizations));
    return organizations[index];
  }

  async deleteOrganization(tenantKey: string): Promise<boolean> {
    const organizations = await this.getOrganizations();
    const org = organizations.find(o => o.tenantKey === tenantKey);
    
    if (!org) return false;
    
    // Eliminar todos los datos de la organización
    localStorage.removeItem(this.getStorageKey('teachers', org._id));
    localStorage.removeItem(this.getStorageKey('students', org._id));
    localStorage.removeItem(this.getStorageKey('courses', org._id));
    
    // Eliminar la organización
    const filtered = organizations.filter(o => o.tenantKey !== tenantKey);
    localStorage.setItem(this.getStorageKey('organizations'), JSON.stringify(filtered));
    
    return true;
  }

  // Profesores
  async getTeachers(organizationId: string): Promise<Teacher[]> {
    // Si tiene token, intentar usar API real
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Obtener el tenantKey de la organización
        const organizations = await this.getOrganizations();
        const org = organizations.find((o: any) => o._id === organizationId);
        if (!org) {
          console.warn('⚠️ Organización no encontrada para ID:', organizationId);
          return [];
        }
        
        const API_BASE_URL = process.env.REACT_APP_API_URL || 
          (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

        const response = await fetch(`${API_BASE_URL}/api/organizations/${org.tenantKey}/users`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Cargados ${data.users.length} usuarios reales de ${org.tenantKey}`);
          
          // Mapear usuarios a formato Teacher
          return data.users.map((user: any) => ({
            _id: user._id,
            username: user.username || user.email,
            email: user.email,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            role: user.role,
            organizationId: organizationId,
            courses: user.allowedCourses || [],
            phone: user.phone || '',
            isActive: user.isActive !== undefined ? user.isActive : true,
            mustChangePassword: user.mustChangePassword !== undefined ? user.mustChangePassword : false,
            password: '***', // No mostrar password real
            lastLogin: user.lastLogin || '',
            createdAt: user.createdAt || new Date().toISOString(),
            updatedAt: user.updatedAt || new Date().toISOString()
          }));
        } else {
          console.warn('⚠️ Error cargando usuarios desde API:', response.statusText);
          // Para admin del sistema, no usar fallback localStorage
          return [];
        }
      } catch (error) {
        console.warn('⚠️ Error cargando usuarios desde API:', error);
        // Para admin del sistema, no usar fallback localStorage
        return [];
      }
    }
    
    // Fallback a localStorage solo para usuarios normales (no admin del sistema)
    console.log('📝 Usuario normal - usando localStorage como fallback');
    const data = localStorage.getItem(this.getStorageKey('teachers', organizationId));
    return data ? JSON.parse(data) : [];
  }

  async createTeacher(organizationId: string, teacherData: Omit<Teacher, '_id' | 'organizationId' | 'createdAt' | 'updatedAt'>): Promise<Teacher> {
    const teachers = await this.getTeachers(organizationId);
    
    const newTeacher: Teacher = {
      ...teacherData,
      _id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
      organizationId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    teachers.push(newTeacher);
    localStorage.setItem(this.getStorageKey('teachers', organizationId), JSON.stringify(teachers));
    
    return newTeacher;
  }

  async updateTeacher(organizationId: string, teacherId: string, updates: Partial<Teacher>): Promise<Teacher | null> {
    const teachers = await this.getTeachers(organizationId);
    const index = teachers.findIndex(t => t._id === teacherId);
    
    if (index === -1) return null;
    
    teachers[index] = {
      ...teachers[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    localStorage.setItem(this.getStorageKey('teachers', organizationId), JSON.stringify(teachers));
    return teachers[index];
  }

  async deleteTeacher(organizationId: string, teacherId: string): Promise<boolean> {
    const teachers = await this.getTeachers(organizationId);
    const filtered = teachers.filter(t => t._id !== teacherId);
    
    if (filtered.length === teachers.length) return false;
    
    localStorage.setItem(this.getStorageKey('teachers', organizationId), JSON.stringify(filtered));
    return true;
  }

  // Estudiantes
  async getStudents(organizationId: string): Promise<Student[]> {
    // Si tiene token, intentar usar API real
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Obtener el tenantKey de la organización
        const organizations = await this.getOrganizations();
        const org = organizations.find((o: any) => o._id === organizationId);
        if (!org) {
          console.warn('⚠️ Organización no encontrada para ID:', organizationId);
          return [];
        }
        
        const API_BASE_URL = process.env.REACT_APP_API_URL || 
          (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

        const response = await fetch(`${API_BASE_URL}/api/organizations/${org.tenantKey}/students`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Cargados ${data.students.length} estudiantes reales de ${org.tenantKey}`);
          
          // Mapear estudiantes a formato Student
          return data.students.map((student: any) => ({
            _id: student._id,
            id: student.id || student._id,
            originalId: student.originalId, // ✅ AGREGADO: Incluir originalId con K
            name: student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim(),
            firstName: student.firstName || '',
            lastName: student.lastName || '',
            course: student.course || '',
            email: student.email || '',
            gender: student.gender || '',
            organizationId: organizationId,
            skillLevels: student.skillLevels || [],
            metadata: student.metadata || {}, // ✅ AGREGADO: Incluir metadata
            createdAt: student.createdAt || new Date().toISOString(),
            updatedAt: student.updatedAt || new Date().toISOString()
          }));
        } else {
          console.warn('⚠️ Error cargando estudiantes desde API:', response.statusText);
          // Para admin del sistema, no usar fallback localStorage
          return [];
        }
      } catch (error) {
        console.warn('⚠️ Error cargando estudiantes desde API:', error);
        // Para admin del sistema, no usar fallback localStorage
        return [];
      }
    }
    
    // Fallback a localStorage solo para usuarios normales (no admin del sistema)
    console.log('📝 Usuario normal - usando localStorage como fallback para estudiantes');
    const data = localStorage.getItem(this.getStorageKey('students', organizationId));
    return data ? JSON.parse(data) : [];
  }

  async createStudent(organizationId: string, studentData: Omit<Student, '_id' | 'organizationId' | 'createdAt' | 'updatedAt'>): Promise<Student> {
    const students = await this.getStudents(organizationId);
    
    const newStudent: Student = {
      ...studentData,
      _id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
      organizationId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    students.push(newStudent);
    localStorage.setItem(this.getStorageKey('students', organizationId), JSON.stringify(students));
    
    return newStudent;
  }

  async updateStudent(organizationId: string, studentId: string, updates: Partial<Student>): Promise<Student | null> {
    const students = await this.getStudents(organizationId);
    const index = students.findIndex(s => s._id === studentId);
    
    if (index === -1) return null;
    
    students[index] = {
      ...students[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    localStorage.setItem(this.getStorageKey('students', organizationId), JSON.stringify(students));
    return students[index];
  }

  async deleteStudent(organizationId: string, studentId: string): Promise<boolean> {
    const students = await this.getStudents(organizationId);
    const filtered = students.filter(s => s._id !== studentId);
    
    if (filtered.length === students.length) return false;
    
    localStorage.setItem(this.getStorageKey('students', organizationId), JSON.stringify(filtered));
    return true;
  }

  // Cursos
  async getCourses(organizationId: string): Promise<Course[]> {
    // Si tiene token, intentar usar API real
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Obtener el tenantKey de la organización
        const organizations = await this.getOrganizations();
        const org = organizations.find((o: any) => o._id === organizationId);
        if (!org) {
          console.warn('⚠️ Organización no encontrada para ID:', organizationId);
          return [];
        }
        
        const API_BASE_URL = process.env.REACT_APP_API_URL || 
          (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

        const response = await fetch(`${API_BASE_URL}/api/organizations/${org.tenantKey}/courses`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Cargados ${data.courses.length} cursos reales de ${org.tenantKey}`);
          
          // Mapear cursos a formato Course
          return data.courses.map((course: any) => ({
            _id: course._id,
            name: course.name || course.courseName || '',
            level: course.level || '',
            teacherIds: course.teacherIds || [],
            studentCount: course.studentCount || 0,
            organizationId: organizationId,
            createdAt: course.createdAt || new Date().toISOString(),
            updatedAt: course.updatedAt || new Date().toISOString()
          }));
        } else {
          console.warn('⚠️ Error cargando cursos desde API:', response.statusText);
          // Para admin del sistema, no usar fallback localStorage
          return [];
        }
      } catch (error) {
        console.warn('⚠️ Error cargando cursos desde API:', error);
        // Para admin del sistema, no usar fallback localStorage
        return [];
      }
    }
    
    // Fallback a localStorage solo para usuarios normales (no admin del sistema)
    console.log('📝 Usuario normal - usando localStorage como fallback para cursos');
    const data = localStorage.getItem(this.getStorageKey('courses', organizationId));
    return data ? JSON.parse(data) : [];
  }

  async createCourse(organizationId: string, courseData: Omit<Course, '_id' | 'organizationId' | 'createdAt' | 'updatedAt' | 'studentCount'>): Promise<Course> {
    const courses = await this.getCourses(organizationId);
    const students = await this.getStudents(organizationId);
    
    const newCourse: Course = {
      ...courseData,
      _id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
      organizationId,
      studentCount: students.filter(s => s.course === courseData.name).length,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    courses.push(newCourse);
    localStorage.setItem(this.getStorageKey('courses', organizationId), JSON.stringify(courses));
    
    return newCourse;
  }

  // Autenticación
  async authenticateUser(email: string, password: string): Promise<{ success: boolean; user?: Teacher; organization?: OrganizationData; message?: string }> {
    // Admin del sistema - usar endpoint específico (el backend valida la contraseña)
    if (email === SYSTEM_ADMIN_EMAIL) {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 
          (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

        const response = await fetch(`${API_BASE_URL}/api/system-admin/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: email,
            password: password
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            // Guardar el token para uso posterior
            localStorage.setItem('token', data.token);
            
            return {
              success: true,
              user: {
                _id: 'system-admin',
                username: 'admin',
                email: SYSTEM_ADMIN_EMAIL,
                firstName: 'Administrador',
                lastName: 'Sistema',
                role: 'admin',
                organizationId: 'system',
                courses: [],
                isActive: true,
                mustChangePassword: false,
                password: '',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              }
            };
          }
        }
      } catch (error) {
        console.error('Error en login system admin via API:', error);
      }

      return { success: false, message: 'Credenciales inválidas' };
    }

    // Extraer dominio del email
    const domain = email.split('@')[1];
    if (!domain) {
      return { success: false, message: 'Email inválido' };
    }

    // Buscar organización por dominio
    const organizations = await this.getOrganizations();
    const organization = organizations.find(org => org.domains.includes(domain));
    
    if (!organization) {
      return { success: false, message: `Organización no encontrada para el dominio: ${domain}. Dominios disponibles: ${organizations.map(o => o.domains.join(', ')).join(' | ')}` };
    }

    if (organization.status === 'suspended' || organization.status === 'inactive') {
      return { success: false, message: 'Organización suspendida o inactiva' };
    }
    
    // Permitir login en organizaciones 'trial' y 'active'
    if (!['trial', 'active'].includes(organization.status)) {
      return { success: false, message: 'Organización suspendida o inactiva' };
    }

    // Buscar usuario en la organización
    const teachers = await this.getTeachers(organization._id);
    const user = teachers.find(t => t.email === email && t.isActive);
    
    if (!user) {
      return { success: false, message: `Usuario no encontrado. Usuarios disponibles en ${organization.displayName}: ${teachers.map(t => t.email).join(', ')}` };
    }

    if (user.password !== password) {
      return { success: false, message: 'Contraseña incorrecta' };
    }

    // Actualizar último login
    await this.updateTeacher(organization._id, user._id, {
      lastLogin: new Date().toISOString()
    });

    return {
      success: true,
      user,
      organization
    };
  }

  // Subida de archivos (simulada)
  async uploadFile(file: File, organizationId: string, type: 'logo' | 'favicon'): Promise<string> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        // En producción esto se subiría a un servicio de archivos
        const fileName = `${organizationId}_${type}_${Date.now()}.${file.name.split('.').pop()}`;
        localStorage.setItem(`file_${fileName}`, dataUrl);
        resolve(`/uploads/${fileName}`);
      };
      reader.readAsDataURL(file);
    });
  }

  async getFile(fileName: string): Promise<string | null> {
    return localStorage.getItem(`file_${fileName.replace('/uploads/', '')}`);
  }

  // Función para verificar datos existentes
  async checkExistingData(): Promise<void> {
    // Mostrar datos reales de MongoDB
    await this.getRealOrganizationsData();
    
    // También mostrar datos locales para comparación
    const organizations = await this.getOrganizations();
    console.log('\n=== DATOS LOCALES (SIMULADOS) ===');
    console.log('Organizaciones:', organizations.length);
    
    for (const org of organizations) {
      console.log(`\n--- ${org.displayName} (${org.tenantKey}) ---`);
      const teachers = await this.getTeachers(org._id);
      const students = await this.getStudents(org._id);
      const courses = await this.getCourses(org._id);
      
      console.log(`Profesores: ${teachers.length}`);
      teachers.forEach(t => console.log(`  - ${t.email} (${t.role})`));
      
      console.log(`Estudiantes: ${students.length}`);
      console.log(`Cursos: ${courses.length}`);
      courses.forEach(c => console.log(`  - ${c.name} (${c.level})`));
    }
    console.log('==================================');
  }

  // Utilidades
  async initializeSystemData(): Promise<void> {
    // Migrar datos del CPV a la nueva estructura si es necesario
    await this.migrateExistingCPVData();
    
    // REINICIALIZACIÓN ÚNICA PARA CARGAR DATOS COMPLETOS DEL CPV
    const shouldReinitialize = !localStorage.getItem('csn_cpv_complete_data_loaded');
    
    if (shouldReinitialize) {
      // Limpiar localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('csn_') || key.startsWith('file_')) {
          localStorage.removeItem(key);
        }
      });
      
      localStorage.setItem('csn_cpv_complete_data_loaded', 'true');
    }
    
    const organizations = await this.getOrganizations();
    
    if (organizations.length === 0) {
      // Crear Colegio Puerto Varas
      const cpvOrg = await this.createOrganization({
        tenantKey: 'cpv',
        displayName: 'Colegio Puerto Varas',
        domains: ['cpv.cl'],
        status: 'active',
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: new Date().toISOString(),
        branding: {
          logoUrl: '/assets/images/logo.png',
          faviconUrl: '/assets/images/favicon.ico',
          colors: {
            primary: '#2563eb',
            secondary: '#059669',
            accent: '#dc2626'
          }
        },
        settings: {
          maxUsers: 30,
          maxStudents: 300,
          features: ['basic_evaluation', 'reports', 'user_management', 'data_import']
        }
      });

      // Crear TODOS los usuarios reales del CPV
      const adminCPV = await this.createTeacher(cpvOrg._id, {
        username: 'admin.cpv',
        email: 'admin@cpv.cl',
        firstName: 'Administrador',
        lastName: 'CPV',
        role: 'admin',
        courses: [],
        isActive: true,
        mustChangePassword: false,
        password: 'admin123'
      });

      // Profesores de Educación Parvularia
      const profesoraPaula = await this.createTeacher(cpvOrg._id, {
        username: 'paula.ramirez',
        email: 'paula.ramirez@cpv.cl',
        firstName: 'Paula',
        lastName: 'Ramírez',
        role: 'teacher',
        courses: ['Pre-Kinder', 'Kinder'],
        phone: '+56 9 8765 4321',
        isActive: true,
        mustChangePassword: false,
        password: 'profesor123'
      });

      // Profesores de Primer Ciclo Básico
      const profesoraMaria = await this.createTeacher(cpvOrg._id, {
        username: 'maria.gonzalez',
        email: 'maria.gonzalez@cpv.cl',
        firstName: 'María',
        lastName: 'González',
        role: 'teacher',
        courses: ['1° Básico', '2° Básico'],
        phone: '+56 9 8765 4322',
        isActive: true,
        mustChangePassword: false,
        password: 'profesor123'
      });

      const profesorCarlos = await this.createTeacher(cpvOrg._id, {
        username: 'carlos.rodriguez',
        email: 'carlos.rodriguez@cpv.cl',
        firstName: 'Carlos',
        lastName: 'Rodríguez',
        role: 'teacher',
        courses: ['3° Básico', '4° Básico'],
        phone: '+56 9 1234 5678',
        isActive: true,
        mustChangePassword: false,
        password: 'profesor123'
      });

      // Profesores de Segundo Ciclo Básico
      const profesoraLucia = await this.createTeacher(cpvOrg._id, {
        username: 'lucia.morales',
        email: 'lucia.morales@cpv.cl',
        firstName: 'Lucía',
        lastName: 'Morales',
        role: 'teacher',
        courses: ['5° Básico', '6° Básico'],
        phone: '+56 9 2345 6789',
        isActive: true,
        mustChangePassword: false,
        password: 'profesor123'
      });

      const profesorAndres = await this.createTeacher(cpvOrg._id, {
        username: 'andres.silva',
        email: 'andres.silva@cpv.cl',
        firstName: 'Andrés',
        lastName: 'Silva',
        role: 'teacher',
        courses: ['7° Básico', '8° Básico'],
        phone: '+56 9 3456 7890',
        isActive: true,
        mustChangePassword: false,
        password: 'profesor123'
      });

      // Profesores de Asignaturas Especiales
      const profesoraClaudia = await this.createTeacher(cpvOrg._id, {
        username: 'claudia.torres',
        email: 'claudia.torres@cpv.cl',
        firstName: 'Claudia',
        lastName: 'Torres',
        role: 'teacher',
        courses: ['Educación Física', 'Deportes'],
        phone: '+56 9 4567 8901',
        isActive: true,
        mustChangePassword: false,
        password: 'profesor123'
      });

      const profesorRicardo = await this.createTeacher(cpvOrg._id, {
        username: 'ricardo.herrera',
        email: 'ricardo.herrera@cpv.cl',
        firstName: 'Ricardo',
        lastName: 'Herrera',
        role: 'teacher',
        courses: ['Música', 'Artes'],
        phone: '+56 9 5678 9012',
        isActive: true,
        mustChangePassword: false,
        password: 'profesor123'
      });

      const profesoraPatricia = await this.createTeacher(cpvOrg._id, {
        username: 'patricia.vega',
        email: 'patricia.vega@cpv.cl',
        firstName: 'Patricia',
        lastName: 'Vega',
        role: 'teacher',
        courses: ['Inglés'],
        phone: '+56 9 6789 0123',
        isActive: true,
        mustChangePassword: false,
        password: 'profesor123'
      });

      // Coordinadores y Personal Administrativo
      const coordinadoraAna = await this.createTeacher(cpvOrg._id, {
        username: 'ana.martinez',
        email: 'ana.martinez@cpv.cl',
        firstName: 'Ana',
        lastName: 'Martínez',
        role: 'viewer',
        courses: [],
        phone: '+56 9 9876 5432',
        isActive: true,
        mustChangePassword: false,
        password: 'coordinador123'
      });

      const coordinadorPedagogico = await this.createTeacher(cpvOrg._id, {
        username: 'fernando.castro',
        email: 'fernando.castro@cpv.cl',
        firstName: 'Fernando',
        lastName: 'Castro',
        role: 'admin',
        courses: [],
        phone: '+56 9 7890 1234',
        isActive: true,
        mustChangePassword: false,
        password: 'coordinador123'
      });

      const psicopedagoga = await this.createTeacher(cpvOrg._id, {
        username: 'elena.rojas',
        email: 'elena.rojas@cpv.cl',
        firstName: 'Elena',
        lastName: 'Rojas',
        role: 'viewer',
        courses: [],
        phone: '+56 9 8901 2345',
        isActive: true,
        mustChangePassword: false,
        password: 'psicopedagoga123'
      });

      // Crear TODOS los cursos reales del CPV
      // Educación Parvularia
      await this.createCourse(cpvOrg._id, {
        name: 'Pre-Kinder',
        level: 'Parvularia',
        teacherIds: [profesoraPaula._id]
      });

      await this.createCourse(cpvOrg._id, {
        name: 'Kinder',
        level: 'Parvularia',
        teacherIds: [profesoraPaula._id]
      });

      // Primer Ciclo Básico
      await this.createCourse(cpvOrg._id, {
        name: '1° Básico',
        level: 'Básico',
        teacherIds: [profesoraMaria._id]
      });

      await this.createCourse(cpvOrg._id, {
        name: '2° Básico',
        level: 'Básico',
        teacherIds: [profesoraMaria._id]
      });

      await this.createCourse(cpvOrg._id, {
        name: '3° Básico',
        level: 'Básico',
        teacherIds: [profesorCarlos._id]
      });

      await this.createCourse(cpvOrg._id, {
        name: '4° Básico',
        level: 'Básico',
        teacherIds: [profesorCarlos._id]
      });

      // Segundo Ciclo Básico
      await this.createCourse(cpvOrg._id, {
        name: '5° Básico',
        level: 'Básico',
        teacherIds: [profesoraLucia._id]
      });

      await this.createCourse(cpvOrg._id, {
        name: '6° Básico',
        level: 'Básico',
        teacherIds: [profesoraLucia._id]
      });

      await this.createCourse(cpvOrg._id, {
        name: '7° Básico',
        level: 'Básico',
        teacherIds: [profesorAndres._id]
      });

      await this.createCourse(cpvOrg._id, {
        name: '8° Básico',
        level: 'Básico',
        teacherIds: [profesorAndres._id]
      });

      // Asignaturas Especiales
      await this.createCourse(cpvOrg._id, {
        name: 'Educación Física',
        level: 'Transversal',
        teacherIds: [profesoraClaudia._id]
      });

      await this.createCourse(cpvOrg._id, {
        name: 'Música',
        level: 'Transversal',
        teacherIds: [profesorRicardo._id]
      });

      await this.createCourse(cpvOrg._id, {
        name: 'Artes',
        level: 'Transversal',
        teacherIds: [profesorRicardo._id]
      });

      await this.createCourse(cpvOrg._id, {
        name: 'Inglés',
        level: 'Transversal',
        teacherIds: [profesoraPatricia._id]
      });

      // Crear TODOS los estudiantes reales del CPV (datos completos)
      const estudiantes = [
        // Pre-Kinder
        { id: '26789012-3', name: 'Emilia Rosales Herrera', firstName: 'Emilia', lastName: 'Rosales Herrera', course: 'Pre-Kinder', gender: 'F', birthDate: '2020-03-15' },
        { id: '26890123-4', name: 'Matías Contreras López', firstName: 'Matías', lastName: 'Contreras López', course: 'Pre-Kinder', gender: 'M', birthDate: '2020-05-22' },
        { id: '26901234-5', name: 'Isadora Mendoza Silva', firstName: 'Isadora', lastName: 'Mendoza Silva', course: 'Pre-Kinder', gender: 'F', birthDate: '2020-07-08' },
        { id: '27012345-6', name: 'Joaquín Vargas Ruiz', firstName: 'Joaquín', lastName: 'Vargas Ruiz', course: 'Pre-Kinder', gender: 'M', birthDate: '2020-01-30' },
        { id: '27123456-7', name: 'Valentina Rojas Mendoza', firstName: 'Valentina', lastName: 'Rojas Mendoza', course: 'Pre-Kinder', gender: 'F', birthDate: '2020-09-12' },

        // Kinder
        { id: '25678901-2', name: 'Sebastián Torres Jiménez', firstName: 'Sebastián', lastName: 'Torres Jiménez', course: 'Kinder', gender: 'M', birthDate: '2019-04-18' },
        { id: '25789012-3', name: 'Sofía Sánchez Flores', firstName: 'Sofía', lastName: 'Sánchez Flores', course: 'Kinder', gender: 'F', birthDate: '2019-06-25' },
        { id: '25890123-4', name: 'Diego Ramírez Vega', firstName: 'Diego', lastName: 'Ramírez Vega', course: 'Kinder', gender: 'M', birthDate: '2019-02-14' },
        { id: '25901234-5', name: 'Catalina Moreno Díaz', firstName: 'Catalina', lastName: 'Moreno Díaz', course: 'Kinder', gender: 'F', birthDate: '2019-08-03' },
        { id: '26012345-6', name: 'Nicolás Guerrero Pinto', firstName: 'Nicolás', lastName: 'Guerrero Pinto', course: 'Kinder', gender: 'M', birthDate: '2019-11-20' },

        // 1° Básico
        { id: '24567890-1', name: 'Antonia Muñoz Herrera', firstName: 'Antonia', lastName: 'Muñoz Herrera', course: '1° Básico', gender: 'F', birthDate: '2018-03-07' },
        { id: '24678901-2', name: 'Benjamín Castillo Ortega', firstName: 'Benjamín', lastName: 'Castillo Ortega', course: '1° Básico', gender: 'M', birthDate: '2018-05-16' },
        { id: '24789012-3', name: 'Florencia Aguilar Ramos', firstName: 'Florencia', lastName: 'Aguilar Ramos', course: '1° Básico', gender: 'F', birthDate: '2018-07-29' },
        { id: '24890123-4', name: 'Tomás Espinoza Navarro', firstName: 'Tomás', lastName: 'Espinoza Navarro', course: '1° Básico', gender: 'M', birthDate: '2018-01-11' },
        { id: '24901234-5', name: 'Martina Contreras Soto', firstName: 'Martina', lastName: 'Contreras Soto', course: '1° Básico', gender: 'F', birthDate: '2018-09-04' },
        { id: '25012345-6', name: 'Agustín Medina Campos', firstName: 'Agustín', lastName: 'Medina Campos', course: '1° Básico', gender: 'M', birthDate: '2018-04-23' },
        { id: '25123456-7', name: 'Isabella Fuentes Bravo', firstName: 'Isabella', lastName: 'Fuentes Bravo', course: '1° Básico', gender: 'F', birthDate: '2018-06-17' },
        { id: '25234567-8', name: 'Vicente Parra Molina', firstName: 'Vicente', lastName: 'Parra Molina', course: '1° Básico', gender: 'M', birthDate: '2018-02-28' },

        // 2° Básico
        { id: '23456789-0', name: 'Amparo Reyes Valdés', firstName: 'Amparo', lastName: 'Reyes Valdés', course: '2° Básico', gender: 'F', birthDate: '2017-08-15' },
        { id: '23567890-1', name: 'Maximiliano Silva Cortés', firstName: 'Maximiliano', lastName: 'Silva Cortés', course: '2° Básico', gender: 'M', birthDate: '2017-10-09' },
        { id: '23678901-2', name: 'Emilia Pérez Morales', firstName: 'Emilia', lastName: 'Pérez Morales', course: '2° Básico', gender: 'F', birthDate: '2017-03-15' },
        { id: '23789012-3', name: 'Mateo Hernández Silva', firstName: 'Mateo', lastName: 'Hernández Silva', course: '2° Básico', gender: 'M', birthDate: '2017-05-22' },
        { id: '23890123-4', name: 'Josefa López Castro', firstName: 'Josefa', lastName: 'López Castro', course: '2° Básico', gender: 'F', birthDate: '2017-07-08' },
        { id: '23901234-5', name: 'Cristóbal Vargas Ruiz', firstName: 'Cristóbal', lastName: 'Vargas Ruiz', course: '2° Básico', gender: 'M', birthDate: '2017-01-30' },
        { id: '24012345-6', name: 'Esperanza Rojas Mendoza', firstName: 'Esperanza', lastName: 'Rojas Mendoza', course: '2° Básico', gender: 'F', birthDate: '2017-09-12' },
        { id: '24123456-7', name: 'Ignacio Torres Jiménez', firstName: 'Ignacio', lastName: 'Torres Jiménez', course: '2° Básico', gender: 'M', birthDate: '2017-04-18' },

        // 3° Básico
        { id: '22345678-9', name: 'Constanza Sánchez Flores', firstName: 'Constanza', lastName: 'Sánchez Flores', course: '3° Básico', gender: 'F', birthDate: '2016-06-25' },
        { id: '22456789-0', name: 'Felipe Ramírez Vega', firstName: 'Felipe', lastName: 'Ramírez Vega', course: '3° Básico', gender: 'M', birthDate: '2016-02-14' },
        { id: '22567890-1', name: 'Magdalena Moreno Díaz', firstName: 'Magdalena', lastName: 'Moreno Díaz', course: '3° Básico', gender: 'F', birthDate: '2016-08-03' },
        { id: '22678901-2', name: 'Rodrigo Guerrero Pinto', firstName: 'Rodrigo', lastName: 'Guerrero Pinto', course: '3° Básico', gender: 'M', birthDate: '2016-11-20' },
        { id: '22789012-3', name: 'Francisca Muñoz Herrera', firstName: 'Francisca', lastName: 'Muñoz Herrera', course: '3° Básico', gender: 'F', birthDate: '2016-03-07' },
        { id: '22890123-4', name: 'Andrés Castillo Ortega', firstName: 'Andrés', lastName: 'Castillo Ortega', course: '3° Básico', gender: 'M', birthDate: '2016-05-16' },
        { id: '22901234-5', name: 'Camila Aguilar Ramos', firstName: 'Camila', lastName: 'Aguilar Ramos', course: '3° Básico', gender: 'F', birthDate: '2016-07-29' },
        { id: '23012345-6', name: 'Lucas Espinoza Navarro', firstName: 'Lucas', lastName: 'Espinoza Navarro', course: '3° Básico', gender: 'M', birthDate: '2016-01-11' },

        // 4° Básico
        { id: '21234567-8', name: 'Javiera Contreras Soto', firstName: 'Javiera', lastName: 'Contreras Soto', course: '4° Básico', gender: 'F', birthDate: '2015-09-04' },
        { id: '21345678-9', name: 'Bastián Medina Campos', firstName: 'Bastián', lastName: 'Medina Campos', course: '4° Básico', gender: 'M', birthDate: '2015-04-23' },
        { id: '21456789-0', name: 'Maite Fuentes Bravo', firstName: 'Maite', lastName: 'Fuentes Bravo', course: '4° Básico', gender: 'F', birthDate: '2015-06-17' },
        { id: '21567890-1', name: 'Alonso Parra Molina', firstName: 'Alonso', lastName: 'Parra Molina', course: '4° Básico', gender: 'M', birthDate: '2015-02-28' },
        { id: '21678901-2', name: 'Trinidad Reyes Valdés', firstName: 'Trinidad', lastName: 'Reyes Valdés', course: '4° Básico', gender: 'F', birthDate: '2015-08-15' },
        { id: '21789012-3', name: 'Gaspar Silva Cortés', firstName: 'Gaspar', lastName: 'Silva Cortés', course: '4° Básico', gender: 'M', birthDate: '2015-10-09' },
        { id: '21890123-4', name: 'Rosario Pérez Morales', firstName: 'Rosario', lastName: 'Pérez Morales', course: '4° Básico', gender: 'F', birthDate: '2015-03-15' },
        { id: '21901234-5', name: 'Esteban Hernández Silva', firstName: 'Esteban', lastName: 'Hernández Silva', course: '4° Básico', gender: 'M', birthDate: '2015-05-22' },

        // 5° Básico
        { id: '20123456-7', name: 'Agustina López Castro', firstName: 'Agustina', lastName: 'López Castro', course: '5° Básico', gender: 'F', birthDate: '2014-07-08' },
        { id: '20234567-8', name: 'Clemente Vargas Ruiz', firstName: 'Clemente', lastName: 'Vargas Ruiz', course: '5° Básico', gender: 'M', birthDate: '2014-01-30' },
        { id: '20345678-9', name: 'Dominga Rojas Mendoza', firstName: 'Dominga', lastName: 'Rojas Mendoza', course: '5° Básico', gender: 'F', birthDate: '2014-09-12' },
        { id: '20456789-0', name: 'Patricio Torres Jiménez', firstName: 'Patricio', lastName: 'Torres Jiménez', course: '5° Básico', gender: 'M', birthDate: '2014-04-18' },
        { id: '20567890-1', name: 'Renata Sánchez Flores', firstName: 'Renata', lastName: 'Sánchez Flores', course: '5° Básico', gender: 'F', birthDate: '2014-06-25' },
        { id: '20678901-2', name: 'Simón Ramírez Vega', firstName: 'Simón', lastName: 'Ramírez Vega', course: '5° Básico', gender: 'M', birthDate: '2014-02-14' },
        { id: '20789012-3', name: 'Violeta Moreno Díaz', firstName: 'Violeta', lastName: 'Moreno Díaz', course: '5° Básico', gender: 'F', birthDate: '2014-08-03' },
        { id: '20890123-4', name: 'Dante Guerrero Pinto', firstName: 'Dante', lastName: 'Guerrero Pinto', course: '5° Básico', gender: 'M', birthDate: '2014-11-20' },

        // 6° Básico
        { id: '19012345-6', name: 'Colomba Muñoz Herrera', firstName: 'Colomba', lastName: 'Muñoz Herrera', course: '6° Básico', gender: 'F', birthDate: '2013-03-07' },
        { id: '19123456-7', name: 'Eliseo Castillo Ortega', firstName: 'Eliseo', lastName: 'Castillo Ortega', course: '6° Básico', gender: 'M', birthDate: '2013-05-16' },
        { id: '19234567-8', name: 'Paloma Aguilar Ramos', firstName: 'Paloma', lastName: 'Aguilar Ramos', course: '6° Básico', gender: 'F', birthDate: '2013-07-29' },
        { id: '19345678-9', name: 'Aurelio Espinoza Navarro', firstName: 'Aurelio', lastName: 'Espinoza Navarro', course: '6° Básico', gender: 'M', birthDate: '2013-01-11' },
        { id: '19456789-0', name: 'Esperanza Contreras Soto', firstName: 'Esperanza', lastName: 'Contreras Soto', course: '6° Básico', gender: 'F', birthDate: '2013-09-04' },
        { id: '19567890-1', name: 'Maximiliano Medina Campos', firstName: 'Maximiliano', lastName: 'Medina Campos', course: '6° Básico', gender: 'M', birthDate: '2013-04-23' },
        { id: '19678901-2', name: 'Catalina Fuentes Bravo', firstName: 'Catalina', lastName: 'Fuentes Bravo', course: '6° Básico', gender: 'F', birthDate: '2013-06-17' },
        { id: '19789012-3', name: 'Tomás Parra Molina', firstName: 'Tomás', lastName: 'Parra Molina', course: '6° Básico', gender: 'M', birthDate: '2013-02-28' },

        // 7° Básico
        { id: '18901234-5', name: 'Antonia Reyes Valdés', firstName: 'Antonia', lastName: 'Reyes Valdés', course: '7° Básico', gender: 'F', birthDate: '2012-08-15' },
        { id: '18012345-6', name: 'Joaquín Silva Cortés', firstName: 'Joaquín', lastName: 'Silva Cortés', course: '7° Básico', gender: 'M', birthDate: '2012-10-09' },
        { id: '18123456-7', name: 'Isidora Pérez Morales', firstName: 'Isidora', lastName: 'Pérez Morales', course: '7° Básico', gender: 'F', birthDate: '2012-03-15' },
        { id: '18234567-8', name: 'Benjamín Hernández Silva', firstName: 'Benjamín', lastName: 'Hernández Silva', course: '7° Básico', gender: 'M', birthDate: '2012-05-22' },
        { id: '18345678-9', name: 'Martina López Castro', firstName: 'Martina', lastName: 'López Castro', course: '7° Básico', gender: 'F', birthDate: '2012-07-08' },
        { id: '18456789-0', name: 'Santiago Vargas Ruiz', firstName: 'Santiago', lastName: 'Vargas Ruiz', course: '7° Básico', gender: 'M', birthDate: '2012-01-30' },
        { id: '18567890-1', name: 'Florencia Rojas Mendoza', firstName: 'Florencia', lastName: 'Rojas Mendoza', course: '7° Básico', gender: 'F', birthDate: '2012-09-12' },
        { id: '18678901-2', name: 'Matías Torres Jiménez', firstName: 'Matías', lastName: 'Torres Jiménez', course: '7° Básico', gender: 'M', birthDate: '2012-04-18' },

        // 8° Básico
        { id: '17789012-3', name: 'Sofía Sánchez Flores', firstName: 'Sofía', lastName: 'Sánchez Flores', course: '8° Básico', gender: 'F', birthDate: '2011-06-25' },
        { id: '17890123-4', name: 'Diego Ramírez Vega', firstName: 'Diego', lastName: 'Ramírez Vega', course: '8° Básico', gender: 'M', birthDate: '2011-02-14' },
        { id: '17901234-5', name: 'Valentina Moreno Díaz', firstName: 'Valentina', lastName: 'Moreno Díaz', course: '8° Básico', gender: 'F', birthDate: '2011-08-03' },
        { id: '17012345-6', name: 'Nicolás Guerrero Pinto', firstName: 'Nicolás', lastName: 'Guerrero Pinto', course: '8° Básico', gender: 'M', birthDate: '2011-11-20' },
        { id: '17123456-7', name: 'Emilia Muñoz Herrera', firstName: 'Emilia', lastName: 'Muñoz Herrera', course: '8° Básico', gender: 'F', birthDate: '2011-03-07' },
        { id: '17234567-8', name: 'Sebastián Castillo Ortega', firstName: 'Sebastián', lastName: 'Castillo Ortega', course: '8° Básico', gender: 'M', birthDate: '2011-05-16' },
        { id: '17345678-9', name: 'Isabella Aguilar Ramos', firstName: 'Isabella', lastName: 'Aguilar Ramos', course: '8° Básico', gender: 'F', birthDate: '2011-07-29' },
        { id: '17456789-0', name: 'Vicente Espinoza Navarro', firstName: 'Vicente', lastName: 'Espinoza Navarro', course: '8° Básico', gender: 'M', birthDate: '2011-01-11' }
      ];

      // Crear todos los estudiantes
      for (const estudianteData of estudiantes) {
        await this.createStudent(cpvOrg._id, {
          id: estudianteData.id,
          name: estudianteData.name,
          firstName: estudianteData.firstName,
          lastName: estudianteData.lastName,
          course: estudianteData.course,
          gender: estudianteData.gender as 'M' | 'F',
          birthDate: estudianteData.birthDate,
          email: `${estudianteData.firstName.toLowerCase()}.${estudianteData.lastName.toLowerCase().replace(' ', '.')}@estudiante.cpv.cl`
        });
      }

      // Crear organización de ejemplo adicional
      const exampleOrg = await this.createOrganization({
        tenantKey: 'ejemplo',
        displayName: 'Colegio de Ejemplo',
        domains: ['ejemplo.cl'],
        status: 'trial',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        branding: {
          logoUrl: '/assets/images/logo.png',
          faviconUrl: '/assets/images/favicon.ico',
          colors: {
            primary: '#1a56db',
            secondary: '#10b981',
            accent: '#f59e0b'
          }
        },
        settings: {
          maxUsers: 50,
          maxStudents: 500,
          features: ['basic_evaluation', 'reports', 'user_management', 'data_import']
        }
      });

      // Crear usuario admin de ejemplo
      await this.createTeacher(exampleOrg._id, {
        username: 'admin',
        email: 'admin@ejemplo.cl',
        firstName: 'Administrador',
        lastName: 'Ejemplo',
        role: 'admin',
        courses: [],
        isActive: true,
        mustChangePassword: false,
        password: 'admin123'
      });
    }
    
    // Verificar datos después de la inicialización
    await this.checkExistingData();
  }
  // Nuevas funciones para obtener datos reales de MongoDB
  async getRealOrganizationsData(): Promise<void> {
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? '' // mismo dominio que el frontend 
        : 'http://localhost:5000';

      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No hay token para obtener datos reales');
        return;
      }

      // Obtener organizaciones reales
      const orgResponse = await fetch(`${API_BASE_URL}/api/organizations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (orgResponse.ok) {
        const orgData = await orgResponse.json();
        if (orgData.success) {
          console.log('=== DATOS REALES DE MONGODB ===');
          console.log('Organizaciones:', orgData.organizations.length);
          
          for (const org of orgData.organizations) {
            console.log(`\n--- ${org.displayName} (${org.tenantKey}) ---`);
            console.log(`Estado: ${org.status}`);
            console.log(`Base de datos: ${org.databaseName}`);
            console.log(`Creada: ${new Date(org.createdAt).toLocaleDateString()}`);
          }
        }
      }

      // Obtener estadísticas reales
      const statsResponse = await fetch(`${API_BASE_URL}/api/dashboard-stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        if (statsData.success) {
          console.log('\n=== ESTADÍSTICAS REALES ===');
          console.log(`Total organizaciones: ${statsData.stats.totalOrganizations}`);
          console.log(`Organizaciones activas: ${statsData.stats.activeOrganizations}`);
          console.log(`Total usuarios: ${statsData.stats.totalUsers}`);
          console.log(`Total estudiantes: ${statsData.stats.totalStudents}`);
        }
      }

      console.log('===============================');
    } catch (error) {
      console.error('Error obteniendo datos reales:', error);
    }
  }

  // Función para migrar datos del CPV a la nueva estructura
  async migrateExistingCPVData(): Promise<void> {
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'production' 
        ? '' // mismo dominio que el frontend 
        : 'http://localhost:5000';

      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No hay token para migrar datos');
        return;
      }

      // Verificar si ya existe la organización CPV
      const orgResponse = await fetch(`${API_BASE_URL}/api/organizations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (orgResponse.ok) {
        const orgData = await orgResponse.json();
        if (orgData.success) {
          const cpvExists = orgData.organizations.some((org: any) => org.tenantKey === 'cpv');
          
          if (!cpvExists) {
            // Crear la organización CPV basada en los datos existentes
            const cpvOrgData = {
              tenantKey: 'cpv',
              displayName: 'Colegio Puerto Varas',
              domains: ['cpv.cl', 'colegiopuertovaras.cl'],
              status: 'active',
              branding: {
                logoUrl: '/logocolegiosinnotas.png',
                faviconUrl: '/favicon.ico',
                colors: {
                  primary: '#1e40af',
                  secondary: '#64748b',
                  accent: '#f59e0b'
                }
              },
              settings: {
                maxUsers: 100,
                maxStudents: 1000,
                features: ['evaluations', 'reports', 'analytics', 'backup']
              },
              adminUser: {
                username: 'admin@cpv.cl',
                email: 'admin@cpv.cl',
                firstName: 'Administrador',
                lastName: 'CPV',
                // Sin contraseña fija en el código público: aleatoria; se restablece desde el administrador del sistema.
                password: Array.from(window.crypto.getRandomValues(new Uint32Array(4)), (n) => n.toString(36)).join('')
              }
            };

            const createResponse = await fetch(`${API_BASE_URL}/api/organizations`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(cpvOrgData)
            });

            if (createResponse.ok) {
              const createData = await createResponse.json();
              if (createData.success) {
                console.log('✅ Organización CPV creada exitosamente');
                console.log('Base de datos:', createData.organization.databaseName);
              }
            }
          } else {
            console.log('ℹ️ La organización CPV ya existe');
          }
        }
      }
    } catch (error) {
      console.error('Error migrando datos CPV:', error);
    }
  }
}

export const databaseService = new DatabaseService();
export default databaseService;